// Translation loader utility for per-page approach with translations in page subdirectories
// All translations are loaded from pages/*/translations/index.js and shared components/*/translations/index.js
// NOTE: This file is in platform-core, but pages are in platform-public/auth, so translations should be loaded
// by the app-specific translation loaders, not from platform-core

// Import shared component translations (Header, Footer, etc.)
import sharedTranslations from 'components/shared/translations';
import logger from './logger';

// Legal page translations (platform-public pages)
import termsTranslations from '../pages/terms/TermsOfService/translations';
import privacyTranslations from '../pages/privacy/PrivacyPolicy/translations';
import gdprTranslations from '../pages/gdpr/GDPR/translations';
import dpaTranslations from '../pages/dpa/DataProcessingAgreement/translations';
import eulaTranslations from '../pages/eula/EULA/translations';
import acceptableUseTranslations from '../pages/acceptableuse/AcceptableUse/translations';
import disclaimerTranslations from '../pages/disclaimer/Disclaimer/translations';
import impressumTranslations from '../pages/impressum/Impressum/translations';
import cookieSettingsTranslations from '../pages/cookies/CookieSettings/translations';
import businessDpaTranslations from '../pages/businessdpa/BusinessDataProcessingAgreement/translations';
import costPoolDetailModalTranslations from '../components/CostPoolDetailModal/translations';

// Deep merge function to combine translation objects
// eslint-disable-next-line no-unused-vars
const deepMerge = (target, source) => {
  const result = { ...target };
  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      result[key] = deepMerge(result[key] || {}, source[key]);
    } else {
      result[key] = source[key];
    }
  }
  return result;
};

// Page translations are app-specific; each app builds and exports its map for registerPageTranslations
// eslint-disable-next-line no-unused-vars
let PAGE_TRANSLATIONS = {};
let PAGE_TRANSLATION_PATHS = {};

export let PAGE_TRANSLATIONS_MAP = {
  terms: termsTranslations,
  privacy: privacyTranslations,
  gdpr: gdprTranslations,
  dpa: dpaTranslations,
  eula: eulaTranslations,
  'acceptable-use': acceptableUseTranslations,
  acceptableuse: acceptableUseTranslations,
  disclaimer: disclaimerTranslations,
  impressum: impressumTranslations,
  'cookie-settings': cookieSettingsTranslations,
  cookies: cookieSettingsTranslations,
  'business-dpa': businessDpaTranslations,
  businessdpa: businessDpaTranslations,
  costPoolDetailModal: costPoolDetailModalTranslations.default || costPoolDetailModalTranslations,
};

// Function to register page translations from app-specific loaders
export const registerPageTranslations = (translationsMap) => {
  PAGE_TRANSLATIONS_MAP = { ...PAGE_TRANSLATIONS_MAP, ...translationsMap };
  // Clear cache when new translations are registered
  Object.keys(pageTranslationCache).forEach(key => {
    delete pageTranslationCache[key];
  });
};

// Load and merge all translation files using per-page approach
// Returns shared component translations (Header, Footer, etc.)
export const loadTranslations = () => {
  // Return shared component translations - these are used across all pages
  // This includes Header, Footer, and other shared component translations
  return sharedTranslations || {};
};

// Cache for dynamically loaded page translations
const pageTranslationCache = {};

// Load page-specific translations from pages/*/translations/index.js
// pageName: string - Name of the page (e.g., 'newsletter', 'about')
// Returns: Promise<object> - Page translations
export const loadPageTranslations = async (pageName) => {
  // Check cache first
  if (pageTranslationCache[pageName]) {
    return pageTranslationCache[pageName];
  }

  // Try to load from page subdirectory
  const loader = PAGE_TRANSLATION_PATHS[pageName];
  if (loader) {
    try {
      const translationModule = await loader();
      const translations = translationModule.default || translationModule;
      
      // Cache the result
      if (translations && Object.keys(translations).length > 0) {
        pageTranslationCache[pageName] = translations;
        return translations;
      }
    } catch (error) {
      logger.warn('TRANSLATION-LOADER', 'Translations not found for page', { pageName, error: error.message });
    }
  }

  // Return empty translations if not found
  return {};
};

// Load page translations synchronously
// Uses static imports for immediate access
// pageName: string - Name of the page
// Returns: object - Page translations
export const loadPageTranslationsSync = (pageName) => {
  // Always load fresh from static imports (don't cache to avoid stale data)
  // The translations themselves are static imports, so this is fast
  const translations = PAGE_TRANSLATIONS_MAP[pageName];
  if (translations) {
    const resolvedTranslations = translations.default || translations;
    if (resolvedTranslations && Object.keys(resolvedTranslations).length > 0) {
      return resolvedTranslations;
    }
  }

  // Return empty translations if not found
  return {};
};

export default loadTranslations;
